import React, { FC, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
	Button,
	Form,
	Spin,
	Col,
	Row,
	Checkbox,
	Typography,
	DatePicker,
	Tooltip,
	message,
} from "antd";
import { CheckboxValueType } from "antd/es/checkbox/Group";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { VideoFormModal } from "../../../utils/StyledComponents";
import { DistributionDataPropsTypes } from "../../../episode";
import { QuestionCircleOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { SelectedVideoData } from "../../videos";
import { authAxios } from "../../../utils/session_utils";

export const ActivatesDatesHeadingContainer = styled.div`
	display: flex;
	justify-content: "center";
	align-items: "center";
	gap: 10px;
`;

export const TooltipContent = styled.div`
	max-width: 400px;

	ul {
		padding-left: 20px;
	}
`;

interface Props extends DistributionDataPropsTypes {
	isOpen: boolean;
	isLoading: boolean;
	handleCancel: () => void;
	distributionPlatform: any[];
	selectedVideoData: SelectedVideoData[];
}

const BulkVideoActivationDatesModal: FC<Props> = ({
	isLoading,
	handleCancel,
	checkAll,
	isOpen,
	indeterminate,
	selectedVideoData,
	distributionPlatform,
	onDistributionCheckAllChange,
	distributionChange,
	distributionCheckedList,
}) => {
	const [isClearActivateDate, setIsClearActivateDate] = useState<boolean>(false);
	const [isClearDeactivateDate, setIsClearDeactivateDate] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [startDate, setStartDate] = useState<string>("");
	const [endDate, setEndDate] = useState<string>("");
	const [startDayJS, setStartDayJS] = useState<Dayjs | Dayjs[]>();
	const [endDayJS, setEndDayJS] = useState<Dayjs | Dayjs[]>();

	const selectAllHandler = (e: CheckboxChangeEvent) => {
		onDistributionCheckAllChange(e);
	};

	const getSingleDayjs = (value: Dayjs | Dayjs[] | undefined): Dayjs | undefined => {
		if (dayjs.isDayjs(value)) return value;
		if (Array.isArray(value) && value.length > 0 && dayjs.isDayjs(value[0])) return value[0];
		return undefined;
	};

	const isUpdateDateButtonEnabled = () => {
		return (
			distributionCheckedList.length &&
			(!!startDate || !!endDate || isClearActivateDate || isClearDeactivateDate)
		);
	};

	const bulkUpdateVideoActivationdates = async () => {
		try {
			setLoading(true);
			const data = {
				video_ids: selectedVideoData.map(video => video.id),
				platform_ids: distributionCheckedList,
				isClearActivateDate,
				isClearDeactivateDate,
				activateDate: startDate,
				deactivateDate: endDate,
			};
			const config = {
				method: "POST",
				url: `${process.env.REACT_APP_BACK_END_API}/snowflake/bulk/update-video-activation-dates`,
				data: JSON.stringify(data),
				headers: { "Content-Type": "application/json" },
			};
			const response = await authAxios(config);
			message.success(response.data.message);
			setLoading(false);
			handleCancel();
		} catch (error) {
			setLoading(false);
			message.error(`Selected videos could not be updated with activation dates.`);
			console.error("error removeBulkVideosFromList", error);
		}
	};

	return (
		<VideoFormModal
			destroyOnClose={true}
			maskClosable={false}
			onCancel={handleCancel}
			centered
			title={<Typography.Title level={3}>Bulk Set Activate and Deactivate</Typography.Title>}
			open={isOpen}
			footer={
				<>
					<Button onClick={handleCancel}>Cancel</Button>
					<Button
						onClick={bulkUpdateVideoActivationdates}
						disabled={!isUpdateDateButtonEnabled()}
						type="primary"
					>
						Update Dates
					</Button>
				</>
			}
			okButtonProps={{ disabled: isLoading }}
			cancelButtonProps={{ disabled: isLoading }}
		>
			<Spin spinning={isLoading || loading}>
				<Row id="video-form-main-row" gutter={[16, 16]}>
					<Col id="distribution-details" span={12}>
						<Typography.Title level={3}>Distribution</Typography.Title>
						<Checkbox indeterminate={indeterminate} onChange={selectAllHandler} checked={checkAll}>
							{checkAll ? "De-Select All" : "Select All"}
						</Checkbox>
						<Checkbox.Group
							onChange={(list: CheckboxValueType[]) => {
								distributionChange(list);
							}}
							value={distributionCheckedList}
						>
							<Row>
								{distributionPlatform.map((platform: any) => {
									return (
										<Col span={12} key={platform.key}>
											<Checkbox value={platform.id} key={platform.key}>
												{platform.name}
											</Checkbox>
										</Col>
									);
								})}
							</Row>
						</Checkbox.Group>
					</Col>
					<Col id="distribution-range-system" span={12}>
						<Typography.Title level={3}>
							<ActivatesDatesHeadingContainer>
								Activate & Deactivate Date
								<Tooltip
									placement="top"
									overlayStyle={{ maxWidth: '500px' }}
									title={
										<TooltipContent>
											<ul>
												<li>
													Empty Activate and Deactivate dates indicate no change. For example, if
													you fill in an Activate Date but leave the Deactivate Date blank, the
													system will only update Activate Dates for selected Videos on the selected
													Distribution Platforms. Any existing Deactivate Dates will remain
													unchanged.
												</li>
												<li>
													If you want to clear the Activate or Deactivate dates, use the “Clear”
													checkboxes. If a checkbox is checked, the date will be ignored and that
													date will be deleted from all the selected Videos on the selected
													Distribution Platforms.
												</li>
												<li>
													You must fill in at least one date, or one“Clear” checkbox, and at least
													one Distribution Platform before the Update Dates button is allowed.
												</li>
											</ul>
										</TooltipContent>
									}
									arrow={true}
								>
									<QuestionCircleOutlined />
								</Tooltip>
							</ActivatesDatesHeadingContainer>
						</Typography.Title>

						<Row>
							<Col span={12}>
								<Form.Item label="Activate">
									<DatePicker
										onChange={(date: Dayjs | null, dateString: string) => {
											setStartDate(dateString);
											setStartDayJS(date ? date : undefined);
										}}
										format={["MM/DD/YYYY"]}
										placeholder="MM/DD/YYYY"
										value={getSingleDayjs(startDayJS)}
										disabled={isClearActivateDate}
									/>
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item label="Deactivate">
									<DatePicker
										onChange={(date: Dayjs | null, dateString: string) => {
											setEndDate(dateString);
											setEndDayJS(date ? date : undefined);
										}}
										format={["MM/DD/YYYY"]}
										placeholder="MM/DD/YYYY"
										value={getSingleDayjs(endDayJS)}
										disabled={isClearDeactivateDate}
									/>
								</Form.Item>
							</Col>
							<Col span={12}>
								<Checkbox onChange={e => setIsClearActivateDate(e.target.checked)}>
									Clear Activate Date
								</Checkbox>
							</Col>
							<Col span={12}>
								<Checkbox onChange={e => setIsClearDeactivateDate(e.target.checked)}>
									Clear Deactivate Date
								</Checkbox>
							</Col>
						</Row>
					</Col>
				</Row>
			</Spin>
		</VideoFormModal>
	);
};

export default BulkVideoActivationDatesModal;
